import React, { useState } from 'react';
import { Download } from 'lucide-react';
import UAD26FormComplete from './UAD26FormComplete';
import UAD36Form from './UAD36Form';

const UADReportView = ({ subjectProperty, selectedComparables, marketData, currentAddress, uploadedImages }) => {
  const [uadVersion, setUadVersion] = useState('2.6');
  const [showReport, setShowReport] = useState(false);
  const [formData26, setFormData26] = useState(null);
  const [formData36, setFormData36] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState([]);

  const handleGenerateReport = async () => {
    if (!subjectProperty && !currentAddress) {
      alert('Please search for a property first');
      return;
    }

    setIsGenerating(true);
    setShowReport(true);

    setTimeout(() => {
      setIsGenerating(false);
    }, 500);
  };

  const handleDownload = async (format) => {
    if (!showReport) {
      alert('Please generate UAD report first');
      return;
    }

    try {
      console.log('📥 Download initiated:', { format, uadVersion });
      console.log('📊 Form Data:', uadVersion === '2.6' ? formData26 : formData36);
      console.log('🏠 Subject Property:', subjectProperty);

      const formData = uadVersion === '2.6' ? formData26 : formData36;

      // Check if we need to use multipart/form-data for file uploads (PDF only)
      const useFileUpload = format === 'pdf' && additionalFiles.length > 0;

      let response;

      if (useFileUpload) {
        // Use multipart/form-data for file uploads
        const formDataObj = new FormData();
        formDataObj.append('version', uadVersion);
        formDataObj.append('subjectProperty', JSON.stringify(subjectProperty));
        formDataObj.append('selectedComparables', JSON.stringify(selectedComparables || []));
        formDataObj.append('marketData', JSON.stringify(marketData || {}));
        formDataObj.append('formData', JSON.stringify(formData || {}));

        // Append files
        additionalFiles.forEach(file => {
          formDataObj.append('additionalFiles', file);
        });

        const endpoint = `/api/uad${uadVersion.replace('.', '')}/export-pdf-with-files`;
        console.log('🌐 Endpoint (with files):', `http://localhost:5000${endpoint}`);
        console.log('📎 Additional Files:', additionalFiles.length);

        response = await fetch(`http://localhost:5000${endpoint}`, {
          method: 'POST',
          body: formDataObj
        });
      } else {
        // ✅ FIX ISSUE #7: Convert uploaded images to base64 for transmission
        let uploadedImagesBase64 = null;
        if (uploadedImages && uploadedImages.length > 0) {
          console.log(`📷 Converting ${uploadedImages.length} uploaded images to base64...`);
          uploadedImagesBase64 = await Promise.all(
            uploadedImages.map(async (img) => {
              const base64 = await fileToBase64(img.file);
              return {
                data: base64,
                name: img.name,
                type: img.file.type
              };
            })
          );
        }

        // Use standard JSON endpoint
        const requestData = {
          version: uadVersion,
          subjectProperty: subjectProperty,
          selectedComparables: selectedComparables || [],
          marketData: marketData || {},
          formData: formData || {},
          uploadedImages: uploadedImagesBase64 // ✅ FIX ISSUE #7: Include uploaded images
        };

        console.log('📤 Request Data:', requestData);

        const endpoint = format === 'xml'
          ? `/api/uad${uadVersion.replace('.', '')}/export-xml`
          : `/api/uad${uadVersion.replace('.', '')}/export-pdf`;

        console.log('🌐 Endpoint:', `http://localhost:5000${endpoint}`);

        response = await fetch(`http://localhost:5000${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData)
        });
      }

      console.log('📨 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to download ${format.toUpperCase()}: ${errorText}`);
      }

      const blob = await response.blob();
      console.log('📦 Blob size:', blob.size, 'bytes');

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const address = currentAddress || subjectProperty?.StreetAddress || subjectProperty?.UnparsedAddress || 'Property';
      a.download = `UAD_${uadVersion}_${address.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`✅ UAD ${uadVersion} ${format.toUpperCase()} downloaded successfully!`);
      alert(`${format.toUpperCase()} file downloaded successfully!`);
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Error downloading ${format.toUpperCase()}: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">UAD Appraisal Report Generator</h2>
            <p className="text-blue-100 mt-1">
              {currentAddress || 'Enter address to begin'}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Version Dropdown */}
            <div className="bg-white rounded-lg p-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">UAD Version</label>
              <select
                value={uadVersion}
                onChange={(e) => setUadVersion(e.target.value)}
                className="px-4 py-2 bg-gray-50 border-2 border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none font-semibold"
              >
                <option value="2.6">UAD 2.6</option>
                <option value="3.6">UAD 3.6.0 (MISMO 3.6)</option>
              </select>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg shadow-lg transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isGenerating ? 'Generating...' : 'Generate UAD Report'}
            </button>

            {/* Download Dropdown */}
            {showReport && (
              <div className="relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleDownload(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-lg shadow-lg cursor-pointer appearance-none pr-10"
                >
                  <option value="">📥 Download</option>
                  <option value="xml">Download XML</option>
                  <option value="pdf">Download PDF</option>
                </select>
                <Download className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white pointer-events-none" />
              </div>
            )}
          </div>
        </div>

        {/* File Upload Section - Only show for UAD 2.6 when report is shown */}
        {showReport && uadVersion === '2.6' && (
          <div className="mt-4 bg-white bg-opacity-20 rounded p-4">
            <h4 className="text-sm font-semibold mb-2">📎 Additional Files (Optional)</h4>
            <p className="text-xs text-blue-100 mb-3">
              Upload PDF, Word documents, or images to append to the end of the report (max 10 files, 10MB each)
            </p>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              multiple
              onChange={(e) => setAdditionalFiles(Array.from(e.target.files))}
              className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-white file:text-blue-700 hover:file:bg-blue-50"
            />
            {additionalFiles.length > 0 && (
              <div className="mt-3 bg-white bg-opacity-90 rounded p-3">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Selected files ({additionalFiles.length}):
                </p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {additionalFiles.map((file, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setAdditionalFiles([])}
                  className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                >
                  Clear all files
                </button>
              </div>
            )}
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-4 bg-blue-500 bg-opacity-50 rounded p-3">
          <p className="text-sm">
            <span className="font-semibold">
              {uadVersion === '2.6' ? 'UAD 2.6' : 'UAD 3.6.0 (MISMO 3.6)'}
            </span>
            {uadVersion === '2.6'
              ? ' - Standard URAR Form 1004 with ~120 fields'
              : ' - Enhanced with Q1-Q6 Quality & C1-C6 Condition ratings, ~180 fields'}
          </p>
        </div>
      </div>

      {/* Report Form */}
      {showReport && (
        <div className="animate-fadeIn">
          {uadVersion === '2.6' ? (
            <UAD26FormComplete
              subjectProperty={subjectProperty}
              selectedComparables={selectedComparables}
              marketData={marketData}
              onUpdate={setFormData26}
            />
          ) : (
            <UAD36Form
              subjectProperty={subjectProperty}
              selectedComparables={selectedComparables}
              marketData={marketData}
              onUpdate={setFormData36}
            />
          )}
        </div>
      )}

      {/* Instructions */}
      {!showReport && (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Report Generated Yet</h3>
          <p className="text-gray-600 mb-4">
            {currentAddress
              ? 'Select UAD version and click "Generate UAD Report" to begin'
              : 'Please search for a property address first, then click "Generate UAD Report"'}
          </p>
          <div className="text-sm text-gray-500 space-y-2">
            <p>• UAD 2.6: Standard URAR Form 1004</p>
            <p>• UAD 3.6.0: Enhanced with granular quality/condition ratings</p>
            <p>• All fields are editable</p>
            <p>• Export as XML or PDF</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ FIX ISSUE #7: Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default UADReportView;
